// Advanced NLP Service for BookAPT
// Handles natural language processing for appointment booking

export interface BookingIntent {
  type: 'book' | 'check_availability' | 'cancel' | 'reschedule' | 'pricing' | 'contact' | 'hours' | 'help' | 'unknown';
  confidence: number;
  entities: {
    service?: string;
    date?: string;
    time?: string;
    duration?: number;
    price?: number;
    contact_method?: string;
  };
  rawText: string;
}

export interface AppointmentRequest {
  service: string;
  preferredDate: string;
  preferredTime: string;
  customerName: string;
  phone: string;
  email: string;
  specialRequests?: string;
}

class NLPService {
  private serviceKeywords = {
    // Automotive Services
    'oil change': ['oil change', 'oil', 'lube', 'oil service', 'synthetic oil', 'oil filter'],
    'car repair': ['car repair', 'auto repair', 'mechanic', 'car service', 'vehicle repair', 'engine'],
    'tire service': ['tire', 'tires', 'tire rotation', 'new tires', 'tire change', 'wheel'],
    'brake service': ['brake', 'brakes', 'brake check', 'brake repair', 'brake pads'],
    
    // Beauty & Wellness
    'haircut': ['haircut', 'cut', 'trim', 'style', 'styling', 'barber', 'hair'],
    'hair_color': ['color', 'dye', 'highlight', 'bleach', 'coloring', 'tint'],
    'facial': ['facial', 'skin', 'treatment', 'cleansing', 'mask', 'skin care'],
    'manicure': ['manicure', 'nails', 'polish', 'nail art', 'gel nails'],
    'pedicure': ['pedicure', 'feet', 'toenails', 'foot care'],
    'massage': ['massage', 'therapy', 'relaxation', 'swedish', 'deep tissue', 'spa'],
    'waxing': ['wax', 'waxing', 'hair removal', 'brazilian', 'bikini'],
    
    // Health & Fitness
    'personal training': ['personal training', 'trainer', 'workout', 'fitness coach', 'gym'],
    'yoga': ['yoga', 'yoga class', 'vinyasa', 'hatha', 'meditation'],
    'pilates': ['pilates', 'pilates class', 'reformer'],
    
    // Medical & Dental
    'doctor': ['doctor', 'physician', 'checkup', 'medical', 'clinic', 'appointment'],
    'dentist': ['dentist', 'dental', 'teeth', 'tooth', 'cavity', 'cleaning'],
    'therapy': ['therapy', 'therapist', 'counseling', 'mental health', 'psychologist'],
    
    // Home & Garden
    'cleaning': ['cleaning', 'clean', 'house cleaning', 'carpet cleaning', 'home cleaning', 'maid'],
    'lawn care': ['lawn', 'mowing', 'grass', 'yard', 'landscaping', 'garden'],
    'plumbing': ['plumber', 'plumbing', 'leak', 'pipe', 'drain', 'toilet', 'faucet'],
    'electrical': ['electrician', 'electrical', 'wiring', 'outlet', 'circuit', 'light'],
    'handyman': ['handyman', 'repairs', 'fix', 'maintenance', 'general contractor'],
    
    // Pet Care
    'dog grooming': ['dog grooming', 'pet grooming', 'dog wash', 'pet bath'],
    'vet': ['vet', 'veterinarian', 'pet doctor', 'animal doctor', 'pet checkup'],
    'dog training': ['dog training', 'pet training', 'obedience', 'puppy training'],
    
    // Technology
    'computer repair': ['computer repair', 'laptop', 'pc', 'mac', 'computer fix'],
    'phone repair': ['phone repair', 'iphone', 'android', 'screen', 'broken phone'],
    'it support': ['IT support', 'tech support', 'help desk', 'network', 'setup'],
    
    // Food & Dining
    'restaurant': ['restaurant', 'dining', 'table', 'reservation', 'dinner'],
    'catering': ['catering', 'caterer', 'event food', 'party food'],
    
    // Education
    'tutoring': ['tutor', 'tutoring', 'lessons', 'teach', 'learning', 'homework help'],
    'music lessons': ['music', 'piano', 'guitar', 'violin', 'singing', 'instrument'],
    
    // Legal
    'lawyer': ['lawyer', 'attorney', 'legal', 'consultation', 'legal advice'],
    'notary': ['notary', 'notarize', 'documents', 'signing'],
    
    // Financial
    'tax': ['tax', 'taxes', 'tax return', 'accountant', 'CPA', 'bookkeeping'],
    'accounting': ['accounting', 'bookkeeper', 'financial'],
    
    // Real Estate
    'real estate': ['real estate', 'realtor', 'house', 'home', 'property', 'viewing'],
    
    // Event Planning
    'wedding': ['wedding', 'wedding planner', 'bride', 'ceremony', 'reception'],
    'party': ['party', 'birthday', 'event', 'celebration', 'party planning'],
    
    // Photography
    'photography': ['photography', 'photographer', 'photo', 'pictures', 'photoshoot'],
    'videography': ['video', 'videographer', 'filming', 'videography'],
    
    // Construction
    'painting': ['painting', 'painter', 'paint', 'interior', 'exterior'],
    'renovation': ['renovation', 'remodel', 'remodeling', 'construction'],
    
    // Moving
    'moving': ['moving', 'movers', 'move', 'relocation', 'packing'],
    
    // Transportation
    'taxi': ['taxi', 'cab', 'ride', 'uber', 'lyft', 'driver'],
    'delivery': ['delivery', 'courier', 'package', 'shipping'],
    
    // Childcare
    'babysitter': ['babysitter', 'babysitting', 'nanny', 'childcare', 'kids'],
    'daycare': ['daycare', 'preschool', 'child care'],
    
    // Security
    'locksmith': ['locksmith', 'lock', 'keys', 'locked out'],
    'security': ['security', 'alarm', 'security system', 'camera'],
    
    // Digital Marketing
    'social media': ['social media', 'instagram', 'facebook', 'marketing', 'ads'],
    'seo': ['SEO', 'search engine', 'google', 'website', 'ranking'],
    
    // Senior Care
    'senior care': ['senior care', 'elderly', 'caregiver', 'home health', 'companion'],
    
    // Sports
    'tennis': ['tennis', 'tennis lessons', 'tennis coach'],
    'golf': ['golf', 'golf lessons', 'golf coach'],
    'swim': ['swim', 'swimming', 'swim lessons', 'swimming lessons'],
    
    // Personal Styling
    'personal shopper': ['personal shopper', 'shopping', 'stylist', 'wardrobe', 'fashion'],
  };

  private timeKeywords = {
    'morning': ['morning', 'am', '9', '10', '11'],
    'afternoon': ['afternoon', 'pm', '12', '1', '2', '3', '4'],
    'evening': ['evening', 'pm', '5', '6', '7', '8'],
  };

  private dateKeywords = {
    'today': ['today', 'now', 'asap'],
    'tomorrow': ['tomorrow', 'next day'],
    'this_week': ['this week', 'week', 'soon'],
    'this_weekend': ['weekend', 'this weekend', 'saturday', 'sunday'],
    'next_week': ['next week', 'following week'],
  };

  // Main NLP processing function
  async processMessage(message: string): Promise<BookingIntent> {
    const normalizedMessage = message.toLowerCase().trim();
    
    // Extract intent
    const intent = this.extractIntent(normalizedMessage);
    
    // Extract entities
    const entities = this.extractEntities(normalizedMessage);
    
    // Calculate confidence
    const confidence = this.calculateConfidence(intent, entities, normalizedMessage);
    
    return {
      type: intent,
      confidence,
      entities,
      rawText: message,
    };
  }

  // Extract the main intent from the message
  private extractIntent(message: string): BookingIntent['type'] {
    const bookingKeywords = ['book', 'schedule', 'appointment', 'reserve', 'make an appointment', 'i need', 'i want', 'looking for', 'get a', 'get an'];
    const availabilityKeywords = ['available', 'availability', 'free', 'slot', 'openings'];
    const cancelKeywords = ['cancel', 'canceled', 'cancelled', 'remove'];
    const rescheduleKeywords = ['reschedule', 'change', 'move', 'postpone'];
    const pricingKeywords = ['price', 'cost', 'how much', 'fee', 'charge'];
    const contactKeywords = ['contact', 'phone', 'call', 'email', 'address'];
    const hoursKeywords = ['hours', 'what time', 'when do you', 'when are you'];
    const helpKeywords = ['help', 'what can you do', 'assist', 'support'];

    // Check for explicit booking keywords first
    if (this.containsAny(message, bookingKeywords)) return 'book';
    
    // If message mentions a service, treat it as a booking intent
    const service = this.extractService(message);
    if (service) {
      // If they mention a service without other clear intents, assume they want to book
      if (!this.containsAny(message, [...pricingKeywords, ...contactKeywords, ...helpKeywords])) {
        return 'book';
      }
    }
    
    if (this.containsAny(message, availabilityKeywords)) return 'check_availability';
    if (this.containsAny(message, cancelKeywords)) return 'cancel';
    if (this.containsAny(message, rescheduleKeywords)) return 'reschedule';
    if (this.containsAny(message, pricingKeywords)) return 'pricing';
    if (this.containsAny(message, contactKeywords)) return 'contact';
    if (this.containsAny(message, hoursKeywords)) return 'hours';
    if (this.containsAny(message, helpKeywords)) return 'help';

    return 'unknown';
  }

  // Extract entities (service, date, time, etc.)
  private extractEntities(message: string): BookingIntent['entities'] {
    const entities: BookingIntent['entities'] = {};

    // Extract service
    entities.service = this.extractService(message);
    
    // Extract date
    entities.date = this.extractDate(message);
    
    // Extract time
    entities.time = this.extractTime(message);
    
    // Extract duration
    entities.duration = this.extractDuration(message);
    
    // Extract price
    entities.price = this.extractPrice(message);
    
    // Extract contact method
    entities.contact_method = this.extractContactMethod(message);

    return entities;
  }

  // Extract service type from message
  private extractService(message: string): string | undefined {
    for (const [service, keywords] of Object.entries(this.serviceKeywords)) {
      if (this.containsAny(message, keywords)) {
        return service;
      }
    }
    return undefined;
  }

  // Extract date from message
  private extractDate(message: string): string | undefined {
    for (const [date, keywords] of Object.entries(this.dateKeywords)) {
      if (this.containsAny(message, keywords)) {
        return date;
      }
    }
    
    // Look for specific dates (e.g., "January 15", "15th", "15/01")
    const datePatterns = [
      /(\d{1,2})\/(\d{1,2})/, // MM/DD or DD/MM
      /(\d{1,2})th/, // 15th
      /january|february|march|april|may|june|july|august|september|october|november|december/i,
    ];
    
    for (const pattern of datePatterns) {
      if (pattern.test(message)) {
        return 'specific_date';
      }
    }
    
    return undefined;
  }

  // Extract time from message
  private extractTime(message: string): string | undefined {
    for (const [time, keywords] of Object.entries(this.timeKeywords)) {
      if (this.containsAny(message, keywords)) {
        return time;
      }
    }
    
    // Look for specific times (e.g., "2:30 PM", "14:30", "2pm")
    const timePatterns = [
      /(\d{1,2}):(\d{2})\s*(am|pm)?/i,
      /(\d{1,2})\s*(am|pm)/i,
    ];
    
    for (const pattern of timePatterns) {
      if (pattern.test(message)) {
        return 'specific_time';
      }
    }
    
    return undefined;
  }

  // Extract duration from message
  private extractDuration(message: string): number | undefined {
    const durationPatterns = [
      /(\d+)\s*(hour|hr|minute|min)/i,
      /(\d+)\s*(min|mins)/i,
    ];
    
    for (const pattern of durationPatterns) {
      const match = message.match(pattern);
      if (match) {
        const value = parseInt(match[1]);
        const unit = match[2].toLowerCase();
        
        if (unit.includes('hour') || unit.includes('hr')) {
          return value * 60; // Convert to minutes
        } else {
          return value; // Already in minutes
        }
      }
    }
    
    return undefined;
  }

  // Extract price from message
  private extractPrice(message: string): number | undefined {
    const pricePattern = /\$?(\d+(?:\.\d{2})?)/;
    const match = message.match(pricePattern);
    return match ? parseFloat(match[1]) : undefined;
  }

  // Extract contact method from message
  private extractContactMethod(message: string): string | undefined {
    if (message.includes('phone') || message.includes('call')) return 'phone';
    if (message.includes('email') || message.includes('@')) return 'email';
    if (message.includes('text') || message.includes('sms')) return 'sms';
    return undefined;
  }

  // Calculate confidence score
  private calculateConfidence(intent: BookingIntent['type'], entities: BookingIntent['entities'], message: string): number {
    let confidence = 0.5; // Base confidence
    
    // Increase confidence based on intent clarity
    if (intent !== 'unknown') confidence += 0.3;
    
    // Increase confidence based on entities found
    const entityCount = Object.values(entities).filter(v => v !== undefined).length;
    confidence += entityCount * 0.1;
    
    // Increase confidence for longer, more specific messages
    if (message.length > 20) confidence += 0.1;
    if (message.length > 50) confidence += 0.1;
    
    // Cap at 1.0
    return Math.min(confidence, 1.0);
  }

  // Helper function to check if message contains any keywords
  private containsAny(message: string, keywords: string[]): boolean {
    return keywords.some(keyword => message.includes(keyword));
  }

  // Generate response based on intent and entities
  async generateResponse(intent: BookingIntent): Promise<{
    text: string;
    type: 'text' | 'appointment' | 'booking' | 'info' | 'confirmation';
    data?: any;
    actions?: string[];
  }> {
    switch (intent.type) {
      case 'book':
        return this.handleBookingIntent(intent);
      case 'check_availability':
        return this.handleAvailabilityIntent(intent);
      case 'cancel':
        return this.handleCancelIntent(intent);
      case 'reschedule':
        return this.handleRescheduleIntent(intent);
      case 'pricing':
        return this.handlePricingIntent(intent);
      case 'contact':
        return this.handleContactIntent(intent);
      case 'hours':
        return this.handleHoursIntent(intent);
      case 'help':
        return this.handleHelpIntent(intent);
      default:
        return this.handleUnknownIntent(intent);
    }
  }

  private async handleBookingIntent(intent: BookingIntent) {
    const { service, date, time } = intent.entities;
    
    if (service) {
      // Import services
      const { hybridBusinessSearchService } = await import('./hybridBusinessSearchService');
      const { UnifiedAuthService } = await import('./unifiedAuthService');
      
      // Get user zipcode from Firestore
      let userZipcode = '76010'; // Default fallback
      try {
        const currentUser = await UnifiedAuthService.getCurrentUser();
        if (currentUser?.address?.zipcode) {
          userZipcode = currentUser.address.zipcode;
          console.log('📍 Using user zipcode from profile:', userZipcode);
        } else {
          console.log('⚠️ User zipcode not found, using default:', userZipcode);
        }
      } catch (error) {
        console.error('Error getting user zipcode:', error);
      }
      
      // Use hybrid search to find businesses (BookAPT + Foursquare)
      const searchResults = await hybridBusinessSearchService.search({
        query: service,
        zipcode: userZipcode, // Use real user zipcode
      });
      
      // Format the date mention
      let dateText = '';
      if (date) {
        if (date === 'this_weekend' || date.includes('weekend')) {
          dateText = 'this weekend';
        } else {
          dateText = date;
        }
      }
      
      // If NO businesses found (even on Foursquare), handle custom service or invite
      if (searchResults.totalFound === 0) {
        return {
          text: `I couldn't find any businesses offering "${service}" services in your area yet${dateText ? ` ${dateText}` : ''}.\n\n💡 **Here's what I can do:**\n\n1. **Invite a business you know** - Tell me the name and we'll invite them to join BookAPT\n2. **Request custom service** - We'll notify nearby businesses about your need\n3. **Search different category** - Try browsing our 23 categories\n4. **Use 30 Sec Booking** - See all available services\n\nDo you know a specific business you'd like us to add?`,
          type: 'appointment',
          data: {
            service,
            date: date || 'flexible',
            time: time || 'flexible',
            isCustomService: true,
            noBusinessesFound: true,
          },
          actions: ['Invite a Business', 'Request Custom Service', 'Browse Categories', 'Use 30 Sec Booking'],
        };
      }
      
      // We found businesses! Show them to user
      const nearbyBusinesses = searchResults.businesses;
      
      let responseText = searchResults.message + `\n\n`;
      
      // Add business details
      nearbyBusinesses.slice(0, 5).forEach((business, index) => {
        const icon = business.isOnBookAPT ? '✅' : '📍';
        const tag = business.isOnBookAPT ? 'On BookAPT - Book instantly!' : '⚠️ Not on BookAPT';
        const distanceText = business.distance ? `${(business.distance / 1000).toFixed(1)} km` : '';
        const ratingText = business.rating ? `⭐ ${business.rating.toFixed(1)}` : '';
        
        responseText += `${icon} **${index + 1}. ${business.name}**\n`;
        responseText += `   ${ratingText}${distanceText ? ` • 📍 ${distanceText}` : ''}\n`;
        responseText += `   ${tag}\n\n`;
      });
      
      if (nearbyBusinesses.length > 5) {
        responseText += `...and ${nearbyBusinesses.length - 5} more\n\n`;
      }
      
      responseText += searchResults.shouldShowInviteOption 
        ? `Tap a business to ${nearbyBusinesses[0]?.isOnBookAPT ? 'book' : 'request your appointment'}.`
        : `Which business would you like to book with?`;
      
      // Create action buttons for each business (max 5)
      const businessActions = nearbyBusinesses.slice(0, 5).map((business, index) => 
        `${index + 1}. ${business.name}`
      );
      
      return {
        text: responseText,
        type: 'appointment',
        data: {
          service,
          date: date || 'flexible',
          time: time || 'flexible',
          nearbyBusinesses,
          step: 'select_business',
        },
        actions: [...businessActions, 'Use 30 Sec Booking'],
      };
    }
    
    return {
      text: "I'd be happy to help you book an appointment! What type of service are you interested in?",
      type: 'appointment',
      data: {
        suggestions: [
          { id: '1', service: 'Haircut & Styling', duration: 60, price: 45 },
          { id: '2', service: 'Hair Color', duration: 120, price: 85 },
          { id: '3', service: 'Facial Treatment', duration: 90, price: 75 },
          { id: '4', service: 'Manicure', duration: 45, price: 35 },
          { id: '5', service: 'Massage Therapy', duration: 60, price: 100 },
        ],
      },
      actions: ['View All Services', 'Check Availability'],
    };
  }

  // Get nearby businesses for a specific service
  private getNearbyBusinessesForService(service: string): any[] {
    // Mock data for nearby businesses based on service type
    const businessesByService: { [key: string]: any[] } = {
      'oil change': [
        { id: 'auto_1', name: 'AutoCare Plus', rating: 4.6, distance: '0.4 mi', category: 'Auto Service', services: ['Oil Change', 'Tire Rotation', 'Brake Service'], waitTime: '15 min', price: '$35', duration: '30 min' },
        { id: 'auto_2', name: 'Quick Lube Express', rating: 4.5, distance: '0.7 mi', category: 'Auto Service', services: ['Express Oil Change', 'Fluid Check'], waitTime: '10 min', price: '$29', duration: '20 min' },
        { id: 'auto_3', name: 'Precision Auto Care', rating: 4.7, distance: '1.1 mi', category: 'Auto Service', services: ['Full Service Oil Change', 'Filter Replacement'], waitTime: '20 min', price: '$45', duration: '40 min' },
      ],
      'car repair': [
        { id: 'auto_1', name: 'AutoCare Plus', rating: 4.6, distance: '0.4 mi', category: 'Auto Service', services: ['Brake Repair', 'Engine Diagnostics', 'Tire Service'], waitTime: '30 min', price: '$85+', duration: '60+ min' },
        { id: 'auto_4', name: 'Master Mechanics', rating: 4.8, distance: '0.9 mi', category: 'Auto Repair', services: ['General Repair', 'Diagnostics'], waitTime: '25 min', price: '$95+', duration: '90+ min' },
      ],
      'tire service': [
        { id: 'auto_1', name: 'AutoCare Plus', rating: 4.6, distance: '0.4 mi', category: 'Auto Service', services: ['Tire Rotation', 'New Tires', 'Wheel Alignment'], waitTime: '20 min', price: '$25', duration: '45 min' },
        { id: 'auto_5', name: 'Tire World', rating: 4.5, distance: '0.6 mi', category: 'Tire Service', services: ['Tire Sales', 'Installation', 'Rotation'], waitTime: '15 min', price: '$30', duration: '45 min' },
      ],
      'massage': [
        { id: 'spa_1', name: 'Serenity Spa & Wellness', rating: 4.9, distance: '0.5 mi', category: 'Spa & Wellness', services: ['Swedish Massage', 'Deep Tissue', 'Hot Stone'], waitTime: '10 min', price: '$100', duration: '60 min' },
        { id: 'spa_2', name: 'Tranquil Touch Massage', rating: 4.8, distance: '0.8 mi', category: 'Massage Therapy', services: ['Therapeutic Massage', 'Sports Massage'], waitTime: '15 min', price: '$90', duration: '60 min' },
        { id: 'spa_3', name: 'Healing Hands Spa', rating: 4.7, distance: '1.2 mi', category: 'Spa & Wellness', services: ['Aromatherapy', 'Couples Massage'], waitTime: '20 min', price: '$120', duration: '90 min' },
      ],
      'haircut': [
        { id: 'salon_1', name: 'Elite Hair Studio', rating: 4.8, distance: '0.3 mi', category: 'Hair Salon', services: ['Haircut', 'Styling', 'Coloring'], waitTime: '5 min', price: '$45', duration: '30 min' },
        { id: 'salon_2', name: 'Modern Cuts', rating: 4.6, distance: '0.6 mi', category: 'Barber Shop', services: ['Men\'s Haircut', 'Beard Trim'], waitTime: '10 min', price: '$35', duration: '30 min' },
        { id: 'salon_3', name: 'Style Studio', rating: 4.7, distance: '0.9 mi', category: 'Hair Salon', services: ['Haircut', 'Blowout'], waitTime: '15 min', price: '$50', duration: '45 min' },
      ],
      'manicure': [
        { id: 'nail_1', name: 'Nail Art Studio', rating: 4.7, distance: '0.4 mi', category: 'Nail Salon', services: ['Manicure', 'Pedicure', 'Gel Nails'], waitTime: '8 min', price: '$25', duration: '30 min' },
        { id: 'nail_2', name: 'Perfect Polish', rating: 4.6, distance: '0.7 mi', category: 'Nail Salon', services: ['Manicure', 'Nail Art'], waitTime: '12 min', price: '$30', duration: '45 min' },
      ],
      'facial': [
        { id: 'spa_1', name: 'Serenity Spa & Wellness', rating: 4.9, distance: '0.5 mi', category: 'Spa & Wellness', services: ['Facial', 'Skin Treatment'], waitTime: '10 min', price: '$80', duration: '60 min' },
        { id: 'spa_4', name: 'Radiant Skin Studio', rating: 4.8, distance: '0.6 mi', category: 'Skin Care', services: ['Custom Facial', 'Anti-Aging Treatment'], waitTime: '15 min', price: '$95', duration: '75 min' },
      ],
      'cleaning': [
        { id: 'clean_1', name: 'Sparkle Clean Services', rating: 4.7, distance: '0.5 mi', category: 'Cleaning', services: ['House Cleaning', 'Carpet Cleaning', 'Window Washing'], waitTime: '1 day', price: '$150', duration: '2-3 hours' },
        { id: 'clean_2', name: 'Fresh Home Cleaners', rating: 4.8, distance: '0.8 mi', category: 'Cleaning', services: ['Deep Cleaning', 'Move-out Cleaning'], waitTime: '2 days', price: '$180', duration: '3-4 hours' },
      ],
      'personal training': [
        { id: 'gym_1', name: 'Fitness First Gym', rating: 4.6, distance: '0.6 mi', category: 'Fitness', services: ['Personal Training', 'Group Classes'], waitTime: '5 min', price: '$75', duration: '60 min' },
      ],
      'yoga': [
        { id: 'yoga_1', name: 'Zen Yoga Studio', rating: 4.8, distance: '0.5 mi', category: 'Fitness', services: ['Yoga Classes', 'Meditation'], waitTime: '10 min', price: '$25', duration: '60 min' },
      ],
      'doctor': [
        { id: 'med_1', name: 'Arlington Medical Center', rating: 4.7, distance: '1.2 mi', category: 'Medical', services: ['Checkups', 'Consultations'], waitTime: '30 min', price: '$150', duration: '30 min' },
      ],
      'dentist': [
        { id: 'dent_1', name: 'Bright Smile Dental', rating: 4.9, distance: '0.8 mi', category: 'Dental', services: ['Cleaning', 'Exam', 'X-rays'], waitTime: '20 min', price: '$120', duration: '60 min' },
      ],
      'plumbing': [
        { id: 'plumb_1', name: 'Quick Fix Plumbing', rating: 4.5, distance: '0.7 mi', category: 'Plumbing', services: ['Leak Repair', 'Drain Cleaning'], waitTime: '2 hours', price: '$85+', duration: '60+ min' },
      ],
      'dog grooming': [
        { id: 'pet_1', name: 'Pampered Paws', rating: 4.7, distance: '0.6 mi', category: 'Pet Grooming', services: ['Bath', 'Haircut', 'Nail Trim'], waitTime: '30 min', price: '$60', duration: '90 min' },
      ],
      'computer repair': [
        { id: 'tech_1', name: 'TechFix Solutions', rating: 4.6, distance: '0.9 mi', category: 'Tech Repair', services: ['Laptop Repair', 'Virus Removal'], waitTime: '1 hour', price: '$95+', duration: '60+ min' },
      ],
      'restaurant': [
        { id: 'rest_1', name: 'The Garden Bistro', rating: 4.8, distance: '0.5 mi', category: 'Restaurant', services: ['Reservations', 'Private Dining'], waitTime: 'Same day', price: '$$', duration: '90 min' },
      ],
      'tutoring': [
        { id: 'tutor_1', name: 'Smart Tutors', rating: 4.7, distance: '0.6 mi', category: 'Tutoring', services: ['Math', 'Science', 'English'], waitTime: '1 day', price: '$50', duration: '60 min' },
      ],
      'lawyer': [
        { id: 'legal_1', name: 'Arlington Legal Services', rating: 4.6, distance: '1.0 mi', category: 'Legal', services: ['Consultation', 'Contract Review'], waitTime: '2 days', price: '$200', duration: '60 min' },
      ],
      'tax': [
        { id: 'tax_1', name: 'Premier Tax & Accounting', rating: 4.8, distance: '0.8 mi', category: 'Accounting', services: ['Tax Prep', 'Bookkeeping'], waitTime: '3 days', price: '$150+', duration: '90 min' },
      ],
      'real estate': [
        { id: 're_1', name: 'Arlington Realty', rating: 4.7, distance: '0.7 mi', category: 'Real Estate', services: ['Property Viewing', 'Consultation'], waitTime: '1 day', price: 'Free', duration: '60 min' },
      ],
      'wedding': [
        { id: 'event_1', name: 'Dream Events Planning', rating: 4.9, distance: '0.9 mi', category: 'Event Planning', services: ['Wedding Planning', 'Coordination'], waitTime: '1 week', price: '$2000+', duration: 'Consultation' },
      ],
      'photography': [
        { id: 'photo_1', name: 'Perfect Moments Photography', rating: 4.8, distance: '0.6 mi', category: 'Photography', services: ['Portraits', 'Events', 'Commercial'], waitTime: '3 days', price: '$300+', duration: '2+ hours' },
      ],
      'painting': [
        { id: 'paint_1', name: 'Pro Painters', rating: 4.6, distance: '1.0 mi', category: 'Painting', services: ['Interior', 'Exterior', 'Commercial'], waitTime: '1 week', price: '$500+', duration: '1+ day' },
      ],
      'moving': [
        { id: 'move_1', name: 'Swift Movers', rating: 4.5, distance: '1.2 mi', category: 'Moving', services: ['Local Moving', 'Packing'], waitTime: '3 days', price: '$300+', duration: '4+ hours' },
      ],
      'taxi': [
        { id: 'trans_1', name: 'City Cab Service', rating: 4.4, distance: '0.3 mi', category: 'Transportation', services: ['Airport Shuttle', 'Local Rides'], waitTime: '5 min', price: '$15+', duration: 'Varies' },
      ],
      'babysitter': [
        { id: 'child_1', name: 'Trusted Sitters', rating: 4.9, distance: '0.5 mi', category: 'Childcare', services: ['Babysitting', 'Nanny Services'], waitTime: '1 day', price: '$20/hr', duration: 'Flexible' },
      ],
      'locksmith': [
        { id: 'lock_1', name: '24/7 Locksmith Services', rating: 4.7, distance: '0.4 mi', category: 'Locksmith', services: ['Lockout', 'Rekey', 'New Locks'], waitTime: '30 min', price: '$75+', duration: '30 min' },
      ],
      'social media': [
        { id: 'market_1', name: 'Digital Boost Marketing', rating: 4.8, distance: '0.7 mi', category: 'Marketing', services: ['Social Media', 'SEO', 'Ads'], waitTime: '2 days', price: '$500+/mo', duration: 'Consultation' },
      ],
      'senior care': [
        { id: 'senior_1', name: 'Caring Companions', rating: 4.9, distance: '0.6 mi', category: 'Senior Care', services: ['Home Health', 'Companion Care'], waitTime: '1 day', price: '$25/hr', duration: 'Flexible' },
      ],
      'tennis': [
        { id: 'sport_1', name: 'Arlington Tennis Academy', rating: 4.7, distance: '1.0 mi', category: 'Sports', services: ['Private Lessons', 'Group Classes'], waitTime: '2 days', price: '$60', duration: '60 min' },
      ],
      'personal shopper': [
        { id: 'style_1', name: 'Style & Grace Consulting', rating: 4.8, distance: '0.8 mi', category: 'Styling', services: ['Personal Shopping', 'Wardrobe Consulting'], waitTime: '3 days', price: '$150', duration: '2 hours' },
      ],
    };

    // Return businesses for the service, or empty array if not found
    return businessesByService[service] || [];
  }

  private handleAvailabilityIntent(intent: BookingIntent) {
    return {
      text: "Here are our available time slots:",
      type: 'booking',
      data: {
        availableSlots: [
          'Today 2:00 PM - 3:00 PM',
          'Today 3:30 PM - 4:30 PM',
          'Today 5:00 PM - 6:00 PM',
          'Tomorrow 10:00 AM - 11:00 AM',
          'Tomorrow 11:30 AM - 12:30 PM',
          'Tomorrow 2:00 PM - 3:00 PM',
          'Tomorrow 4:00 PM - 5:00 PM',
        ],
      },
      actions: ['Book Now', 'More Times', 'Different Date'],
    };
  }

  private handleCancelIntent(intent: BookingIntent) {
    return {
      text: "I can help you cancel your appointment. Do you have your appointment confirmation number, or would you like me to look it up by your phone number?",
      type: 'text',
      actions: ['Cancel Appointment', 'Look Up Appointment', 'Contact Support'],
    };
  }

  private handleRescheduleIntent(intent: BookingIntent) {
    return {
      text: "I'd be happy to help you reschedule your appointment. When would you like to move it to?",
      type: 'appointment',
      actions: ['Select New Time', 'View Availability', 'Contact Support'],
    };
  }

  private handlePricingIntent(intent: BookingIntent) {
    return {
      text: "Here are our current service prices:",
      type: 'info',
      data: {
        services: [
          { name: 'Haircut & Styling', price: '$45', duration: '60 min' },
          { name: 'Hair Color', price: '$85', duration: '120 min' },
          { name: 'Facial Treatment', price: '$75', duration: '90 min' },
          { name: 'Manicure', price: '$35', duration: '45 min' },
          { name: 'Pedicure', price: '$45', duration: '60 min' },
        ],
      },
      actions: ['Book Service', 'View All Services', 'Ask Questions'],
    };
  }

  private handleContactIntent(intent: BookingIntent) {
    return {
      text: "You can reach us at:\n📞 (555) 123-4567\n📧 info@bookapt.io\n📍 123 Main Street, Arlington, TX\n💬 Or chat with me anytime!",
      type: 'info',
      actions: ['Call Now', 'Send Email', 'Get Directions'],
    };
  }

  private handleHoursIntent(intent: BookingIntent) {
    return {
      text: "Our business hours are:\n🕒 Monday - Friday: 9:00 AM - 7:00 PM\n🕒 Saturday: 9:00 AM - 5:00 PM\n🕒 Sunday: Closed\n\nWe're open now! 🎉",
      type: 'info',
      actions: ['Book Now', 'Check Availability', 'Contact Us'],
    };
  }

  private handleHelpIntent(intent: BookingIntent) {
    return {
      text: "I can help you with:\n• 📅 Booking appointments\n• ⏰ Checking availability\n• 💰 Pricing information\n• 📞 Contact details\n• 🕒 Business hours\n• ❌ Canceling/rescheduling\n\nWhat would you like to do?",
      type: 'info',
      actions: ['Book Appointment', 'Check Availability', 'View Prices', 'Contact Us'],
    };
  }

  private handleUnknownIntent(intent: BookingIntent) {
    return {
      text: "I'm not sure I understand. I can help you book appointments, check availability, or answer questions about our services. What would you like to do?",
      type: 'text',
      actions: ['Book Appointment', 'Check Availability', 'Get Help'],
    };
  }

  // Create appointment from parsed intent
  async createAppointmentFromIntent(intent: BookingIntent, customerDetails: {
    name: string;
    phone: string;
    email: string;
  }): Promise<AppointmentRequest | null> {
    if (intent.type !== 'book' || !intent.entities.service) {
      return null;
    }

    return {
      service: intent.entities.service,
      preferredDate: intent.entities.date || 'flexible',
      preferredTime: intent.entities.time || 'flexible',
      customerName: customerDetails.name,
      phone: customerDetails.phone,
      email: customerDetails.email,
      specialRequests: intent.rawText,
    };
  }
}

export default new NLPService();
